import { useEffect, useState, useCallback } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Detect if running inside a Capacitor native app
 */
const isNativeApp = (): boolean => {
  // Check for Capacitor
  if (typeof (window as any).Capacitor !== 'undefined') {
    return (window as any).Capacitor.isNativePlatform?.() ?? false;
  }
  // Check for standalone mode (PWA installed)
  if (window.matchMedia?.('(display-mode: standalone)').matches) {
    return true;
  }
  // Check iOS standalone
  if ((navigator as any).standalone === true) {
    return true;
  }
  return false;
};

/**
 * Full-screen connection error overlay for native app experience
 * Only activates in native/PWA context, not in regular browser
 */
export const NativeConnectionError = () => {
  const [showError, setShowError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isNative, setIsNative] = useState(false);
  const { language } = useLanguage();

  const translations = {
    en: {
      title: "Connection Error",
      message: "Please check your internet connection and try again.",
      retry: "Try Again",
      retrying: "Connecting..."
    },
    es: {
      title: "Error de Conexión",
      message: "Por favor verifica tu conexión a internet e intenta de nuevo.",
      retry: "Reintentar",
      retrying: "Conectando..."
    }
  };

  const t = translations[language as keyof typeof translations] || translations.en;

  // Detect native context on mount
  useEffect(() => {
    setIsNative(isNativeApp());
  }, []);

  // Check connection on mount and when coming online - ONLY for native apps
  useEffect(() => {
    // Skip connectivity check if not in native app context
    if (!isNative) {
      return;
    }

    const checkConnection = async () => {
      // First check navigator.onLine
      if (!navigator.onLine) {
        setShowError(true);
        return;
      }

      try {
        // Try to fetch a small resource to verify actual connectivity
        // Use GET instead of HEAD to avoid CORS preflight issues
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        const response = await fetch('/manifest-version.json?check=' + Date.now(), {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store'
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          setShowError(false);
        } else {
          setShowError(true);
        }
      } catch {
        // Only show error if we're supposed to be online but can't reach server
        if (navigator.onLine) {
          setShowError(true);
        }
      }
    };

    // Initial check after a small delay to allow app to initialize
    const initialCheck = setTimeout(checkConnection, 3000);

    const handleOnline = () => {
      setShowError(false);
      checkConnection();
    };

    const handleOffline = () => {
      setShowError(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearTimeout(initialCheck);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isNative]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    
    // Simple approach: just reload the page
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  // Don't show in non-native context or if no error
  if (!isNative || !showError) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-[999999] flex flex-col items-center justify-center p-6"
      style={{ backgroundColor: '#0F172A' }}
    >
      <div className="flex flex-col items-center text-center max-w-sm">
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: 'rgba(155, 135, 245, 0.1)' }}
        >
          <WifiOff className="w-10 h-10" style={{ color: '#9b87f5' }} />
        </div>
        
        <h1 
          className="text-2xl font-bold mb-3"
          style={{ color: '#FFFFFF' }}
        >
          {t.title}
        </h1>
        
        <p 
          className="text-base mb-8"
          style={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          {t.message}
        </p>
        
        <Button
          onClick={handleRetry}
          disabled={isRetrying}
          className="min-w-[160px] h-12 text-base font-medium"
          style={{ 
            backgroundColor: '#9b87f5',
            color: '#FFFFFF'
          }}
        >
          {isRetrying ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              {t.retrying}
            </>
          ) : (
            <>
              <RefreshCw className="w-5 h-5 mr-2" />
              {t.retry}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
